import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthUserInfo } from '../api';

/**
 * TypeScript interfaces for the new data sync architecture
 * These interfaces define the data structures used across the sync services
 */

// Database configuration
export const SYNC_DATABASE_CONFIG = {
  name: 'BELL_SUPPLY_SYNC_DB',
  version: 16,
  stores: {
    auth: 'auth',
    api_config: 'api_config',
    sync_metadata: 'sync_metadata',
    background_sync_queue: 'background_sync_queue',
    order_drafts: 'order_drafts',
  },
};

export const TABLE_STORES: { [key: string]: TableStore } = {
  customers: {
    storeName: 'customers',
    keyField: 'id',
    syncEndpoint: '/NetSuiteMobile/GetCustomerInfo',
    syncMode: 'replace',
  },
  locations: {
    storeName: 'locations',
    keyField: 'id_1',
    syncEndpoint: '/NetSuiteMobile/UserLocationSync',
    syncMode: 'replace',
  },
  inventory: {
    storeName: 'inventory',
    keyField: 'CompositeKey',
    syncEndpoint: '/NetSuiteMobile/UserLocationInventoryRead',
    syncMode: 'replace',
    indexes: [
      { name: 'LocationID', keyPath: 'LocationID', unique: false },
      { name: 'PartNumber', keyPath: 'PartNumber', unique: false },
      { name: 'BinNumber', keyPath: 'BinNumber', unique: false },
      { name: 'Description', keyPath: 'Description', unique: false },
    ],
  },
  cost_centers: {
    storeName: 'cost_centers',
    keyField: 'Code',
    syncEndpoint: '/NetSuiteMobile/GetCostCenters',
    syncMode: 'replace',
    indexes: [
      { name: 'CustomerID', keyPath: 'CustomerID', unique: false },
    ],
  },
};

// Index definition interface
export interface IndexDefinition {
  name: string;
  keyPath: string | string[];
  unique?: boolean;
}

export interface TableStore {
  storeName: string;
  keyField: string;
  syncEndpoint: string;
  syncMode?: 'upsert' | 'replace';
  indexes?: IndexDefinition[];
  safetyOptions?: {
    skipSafetyChecks?: boolean;
  };
}

// API Configuration interface
export interface ApiConfig {
  baseUrl: string;
  timeout?: number;
}

// Auth Configuration interface
export interface AuthConfig {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  userInfo?: AuthUserInfo;
}

// Enhanced Sync Metadata interface
export interface SyncMetadata {
  storeName: string; // Name of the store to sync
  lastSyncTime: Date; // Last successful sync timestamp
  lastSyncStatus: string; // Status of last sync attempt
  keyField: string; // Primary key field for upsert operations
  syncEndpoint: string; // API endpoint for syncing this store
}

// Background Sync Queue interface
export interface BackgroundSyncQueueItem {
  id?: string; // Auto-generated ID
  timestamp: Date; // When the request was queued
  endpoint: string; // API endpoint to call
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'; // HTTP method
  object: any; // Data payload to send
  retryCount?: number; // Number of retry attempts
  lastError?: string; // Last error message if any
}

// Draft types for autosave
export type DraftType = 'pick' | 'return';

// In-progress item form state (quantity/notes the user has entered but not yet "Added")
export interface ItemFormState {
  quantity: number;
  notes: string;
}

// Order draft stored in IndexedDB for crash recovery
export interface OrderDraft {
  key: string;
  draftType: DraftType;
  userName: string;
  currentStep: string;
  workflowData: Record<string, unknown>;
  currentPartNumber: string | null;
  currentItemFormState: ItemFormState | null;
  updatedAt: Date;
}

// Sync status for reporting
export interface SyncStatus {
  isOnline: boolean;
  lastSyncTime?: Date;
  syncInProgress: boolean;
  error?: string;
  queuedItemsCount: number;
}

// Health check result
export interface HealthStatus {
  api: boolean;
  database: boolean;
  timestamp: Date;
}

// Configuration status
export interface ConfigurationStatus {
  hasApiConfig: boolean;
  syncMetadataCount: number;
  queuedItemsCount: number;
}

// Generic response wrapper
export interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// Upsert operation result
export interface UpsertResult {
  inserted: number;
  updated: number;
  errors: number;
}

// Filter operator types
export type FilterOperator =
  | 'contains'
  | 'equals'
  | 'startsWith'
  | 'endsWith'
  | 'greaterThan'
  | 'lessThan'
  | 'greaterThanOrEqual'
  | 'lessThanOrEqual'
  | 'notEquals'
  | 'notContains';

// Individual filter condition
export interface FilterCondition {
  field: string;
  operator: FilterOperator;
  value: any;
  caseSensitive?: boolean; // Default false for string operations
}

// Complex filter structure supporting AND/OR logic
export interface ComplexFilter {
  operator: 'AND' | 'OR';
  conditions?: FilterCondition[];
  groups?: ComplexFilter[];
}

// Union type for filter input - supports both simple and complex formats
export type FilterInput =
  | { [fieldName: string]: string } // Simple format (backward compatibility)
  | ComplexFilter; // Complex format with AND/OR logic

@Injectable({
  providedIn: 'root',
})
export class IndexedDbService {
  private db: IDBDatabase | null = null;
  private dbName: string = SYNC_DATABASE_CONFIG.name;
  private dbVersion: number = SYNC_DATABASE_CONFIG.version;
  private initializationPromise: Promise<void> | null = null;

  // Status observables
  private dbStatusSubject = new BehaviorSubject<{
    connected: boolean;
    error?: string;
  }>({ connected: false });
  public dbStatus$ = this.dbStatusSubject.asObservable();

  constructor() {
    // Auto-initialize the database
    this.initDB();
  }

  /**
   * Initialize the database connection
   */
  async initDB(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  private async performInitialization(): Promise<void> {
    if (this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        const error = `IndexedDB connection failed: ${request.error}`;
        console.error(error);
        this.dbStatusSubject.next({ connected: false, error });
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB connection established');
        this.dbStatusSubject.next({ connected: true });
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = (event.target as IDBOpenDBRequest).transaction!;
        this.createStores(db, transaction);
      };
    });
  }

  /**
   * Create object stores during database upgrade
   */
  private createStores(db: IDBDatabase, transaction: IDBTransaction): void {
    // Create auth store
    if (!db.objectStoreNames.contains(SYNC_DATABASE_CONFIG.stores.auth)) {
      db.createObjectStore(SYNC_DATABASE_CONFIG.stores.auth);
    }

    // Create api_config store
    if (!db.objectStoreNames.contains(SYNC_DATABASE_CONFIG.stores.api_config)) {
      db.createObjectStore(SYNC_DATABASE_CONFIG.stores.api_config);
    }

    // Create sync_metadata store with storeName as key
    if (
      !db.objectStoreNames.contains(SYNC_DATABASE_CONFIG.stores.sync_metadata)
    ) {
      const metadataStore = db.createObjectStore(
        SYNC_DATABASE_CONFIG.stores.sync_metadata,
        { keyPath: 'storeName' },
      );
      // Add indexes for common queries
      metadataStore.createIndex('lastSyncTime', 'lastSyncTime', {
        unique: false,
      });
      metadataStore.createIndex('lastSyncStatus', 'lastSyncStatus', {
        unique: false,
      });
    }

    // Create background_sync_queue store with auto-incrementing key
    if (
      !db.objectStoreNames.contains(
        SYNC_DATABASE_CONFIG.stores.background_sync_queue,
      )
    ) {
      const queueStore = db.createObjectStore(
        SYNC_DATABASE_CONFIG.stores.background_sync_queue,
        {
          keyPath: 'id',
          autoIncrement: true,
        },
      );
      // Add indexes for common queries
      queueStore.createIndex('timestamp', 'timestamp', { unique: false });
      queueStore.createIndex('endpoint', 'endpoint', { unique: false });
    }

    // Create order_drafts store for autosave
    if (
      !db.objectStoreNames.contains(SYNC_DATABASE_CONFIG.stores.order_drafts)
    ) {
      db.createObjectStore(SYNC_DATABASE_CONFIG.stores.order_drafts, {
        keyPath: 'key',
      });
    }

    // Create table stores
    Object.values(TABLE_STORES).forEach((table) => {
      let needsRecreation = false;
      let storeForIndexes: IDBObjectStore | null = null;

      if (db.objectStoreNames.contains(table.storeName)) {
        // Store exists, check if schema matches
        const existingStore = transaction.objectStore(table.storeName);

        // Check if keyPath matches
        if (existingStore.keyPath !== table.keyField) {
          needsRecreation = true;
        }

        // Check if indexes match
        if (!needsRecreation && table.indexes) {
          const existingIndexNames = Array.from(existingStore.indexNames);
          const requiredIndexNames = table.indexes.map((index) => index.name);

          // Check if we have different number of indexes or different index names
          if (
            existingIndexNames.length !== requiredIndexNames.length ||
            !requiredIndexNames.every((name) =>
              existingIndexNames.includes(name),
            )
          ) {
            needsRecreation = true;
          }

          // Check if existing indexes have correct properties
          if (!needsRecreation) {
            for (const indexConfig of table.indexes) {
              try {
                const existingIndex = existingStore.index(indexConfig.name);
                if (
                  JSON.stringify(existingIndex.keyPath) !==
                    JSON.stringify(indexConfig.keyPath) ||
                  existingIndex.unique !== (indexConfig.unique || false)
                ) {
                  needsRecreation = true;
                  break;
                }
              } catch {
                // Index doesn't exist
                needsRecreation = true;
                break;
              }
            }
          }
        }

        if (needsRecreation) {
          console.log(
            `Recreating store ${table.storeName} due to schema changes`,
          );
          db.deleteObjectStore(table.storeName);
          storeForIndexes = db.createObjectStore(table.storeName, {
            keyPath: table.keyField,
          });
        }
      } else {
        // Store doesn't exist, create it
        storeForIndexes = db.createObjectStore(table.storeName, {
          keyPath: table.keyField,
        });
      }

      // Create indexes if we created or recreated the store
      if (storeForIndexes && table.indexes) {
        table.indexes.forEach((index) => {
          storeForIndexes!.createIndex(index.name, index.keyPath, {
            unique: index.unique || false,
          });
        });
      }
    });

    console.log('IndexedDB stores created');
  }

  /**
   * Ensure database is initialized before operations
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.db) {
      await this.initDB();
    }
  }

  /**
   * Get all records from a store
   */
  async getAll<T>(storeName: string): Promise<T[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onerror = () => {
        console.error(
          `Error getting all items from ${storeName}:`,
          request.error,
        );
        reject(new Error(`Failed to get all items from ${storeName}`));
      };

      request.onsuccess = () => {
        resolve(request.result || []);
      };
    });
  }

  /**
   * Get single record
   */
  async get<T>(storeName: string, key: string | number): Promise<T | null> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onerror = () => {
        console.error(`Error getting item from ${storeName}:`, request.error);
        reject(new Error(`Failed to get item from ${storeName}`));
      };

      request.onsuccess = () => {
        resolve(request.result || null);
      };
    });
  }

  /**
   * Insert/update record
   */
  async put<T>(
    storeName: string,
    data: T,
    key?: string | number,
  ): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);

      const request = store.keyPath ? store.put(data) : store.put(data, key!);

      request.onerror = () => {
        console.error(`Error putting item to ${storeName}:`, request.error);
        reject(new Error(`Failed to put item to ${storeName}`));
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  /**
   * Delete record
   */
  async delete(storeName: string, key: string | number): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onerror = () => {
        console.error(`Error deleting item from ${storeName}:`, request.error);
        reject(new Error(`Failed to delete item from ${storeName}`));
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  /**
   * Clear all records from store
   */
  async clear(storeName: string): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onerror = () => {
        console.error(`Error clearing store ${storeName}:`, request.error);
        reject(new Error(`Failed to clear store ${storeName}`));
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  /**
   * Update if exists, insert if not (upsert operation)
   */
  async upsert<T extends Record<string, any>>(
    storeName: string,
    data: T[],
    keyField: string,
  ): Promise<UpsertResult> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);

      let inserted = 0;
      let updated = 0;
      let errors = 0;
      let completed = 0;

      if (data.length === 0) {
        resolve({ inserted: 0, updated: 0, errors: 0 });
        return;
      }

      data.forEach((item) => {
        const key = item[keyField];

        // First, check if the item exists
        const getRequest = store.get(key);

        getRequest.onerror = () => {
          errors++;
          completed++;
          if (completed === data.length) {
            resolve({ inserted, updated, errors });
          }
        };

        getRequest.onsuccess = () => {
          const existingItem = getRequest.result;
          const putRequest = store.put(item);

          putRequest.onerror = () => {
            errors++;
            completed++;
            if (completed === data.length) {
              resolve({ inserted, updated, errors });
            }
          };

          putRequest.onsuccess = () => {
            if (existingItem) {
              updated++;
            } else {
              inserted++;
            }
            completed++;
            if (completed === data.length) {
              resolve({ inserted, updated, errors });
            }
          };
        };
      });
    });
  }

  /**
   * Get records by index
   */
  async getByIndex<T>(
    storeName: string,
    indexName: string,
    value: any,
  ): Promise<T[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);

      if (!store.indexNames.contains(indexName)) {
        reject(
          new Error(`Index ${indexName} does not exist in store ${storeName}`),
        );
        return;
      }

      const index = store.index(indexName);
      const request = index.getAll(value);

      request.onerror = () => {
        console.error(
          `Error getting items by index from ${storeName}:`,
          request.error,
        );
        reject(new Error(`Failed to get items by index from ${storeName}`));
      };

      request.onsuccess = () => {
        resolve(request.result || []);
      };
    });
  }

  /**
   * Count records in a store
   */
  async count(storeName: string): Promise<number> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.count();

      request.onerror = () => {
        console.error(`Error counting items in ${storeName}:`, request.error);
        reject(new Error(`Failed to count items in ${storeName}`));
      };

      request.onsuccess = () => {
        resolve(request.result);
      };
    });
  }

  /**
   * Transaction wrapper for multiple operations
   */
  async transaction<T>(
    storeNames: string[],
    mode: IDBTransactionMode,
    operation: (stores: { [key: string]: IDBObjectStore }) => Promise<T>,
  ): Promise<T> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(storeNames, mode);

      transaction.onerror = () => {
        reject(new Error('Transaction failed'));
      };

      transaction.oncomplete = () => {
        // Transaction completed successfully
      };

      const stores: { [key: string]: IDBObjectStore } = {};
      storeNames.forEach((name) => {
        stores[name] = transaction.objectStore(name);
      });

      operation(stores).then(resolve).catch(reject);
    });
  }

  /**
   * Close the database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.initializationPromise = null;
      this.dbStatusSubject.next({ connected: false });
      console.log('IndexedDB connection closed');
    }
  }

  /**
   * Get database status
   */
  isConnected(): boolean {
    return this.db !== null;
  }

  /**
   * Atomic replace operation - replaces all data in store without clearing it first
   * This ensures the store is never empty during the operation
   * Includes safety check to prevent accidental clearing from empty responses
   */
  async atomicReplace<T extends Record<string, any>>(
    storeName: string,
    newData: T[],
    keyField: string,
    options?: {
      skipSafetyChecks?: boolean;
    },
  ): Promise<{
    inserted: number;
    updated: number;
    deleted: number;
    errors: number;
  }> {
    await this.ensureInitialized();

    const skipSafetyChecks = options?.skipSafetyChecks || false;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);

      let inserted = 0;
      let updated = 0;
      let deleted = 0;
      let errors = 0;

      // Step 1: Get all existing keys and current count
      const existingKeysRequest = store.getAllKeys();

      existingKeysRequest.onerror = () => {
        console.error(
          `Error getting existing keys from ${storeName}:`,
          existingKeysRequest.error,
        );
        reject(new Error(`Failed to get existing keys from ${storeName}`));
      };

      existingKeysRequest.onsuccess = () => {
        const existingKeys = new Set(existingKeysRequest.result);
        const newKeys = new Set(newData.map((item) => item[keyField]));
        const existingCount = existingKeys.size;

        // Keys to delete (exist in store but not in new data)
        const keysToDelete = Array.from(existingKeys).filter(
          (key) => !newKeys.has(key),
        );
        const deletionCount = keysToDelete.length;

        // Safety check for suspicious empty response
        if (!skipSafetyChecks && existingCount > 0) {
          if (newData.length === 0 && existingCount > 10) {
            console.warn(
              `⚠️  Atomic replace safety check: Received empty data for ${storeName} with ${existingCount} existing records. Skipping replacement.`,
            );
            reject(
              new Error(
                `Safety check failed: Empty response would delete all ${existingCount} records in ${storeName}`,
              ),
            );
            return;
          }
        }

        // Log the operation details
        console.log(`🔄 Atomic replace starting for ${storeName}:`, {
          existingRecords: existingCount,
          newRecords: newData.length,
          toInsert: Math.max(
            0,
            newData.length - (existingCount - deletionCount),
          ),
          toUpdate: Math.max(0, existingCount - deletionCount),
          toDelete: deletionCount,
        });

        let operationsCompleted = 0;
        const totalOperations = newData.length + keysToDelete.length;

        const checkCompletion = () => {
          operationsCompleted++;
          if (operationsCompleted === totalOperations) {
            console.log(`✅ Atomic replace completed for ${storeName}:`, {
              inserted,
              updated,
              deleted,
              errors,
            });
            resolve({ inserted, updated, deleted, errors });
          }
        };

        // Handle empty operations case
        if (totalOperations === 0) {
          console.log(
            `✅ Atomic replace completed for ${storeName}: No operations needed`,
          );
          resolve({ inserted: 0, updated: 0, deleted: 0, errors: 0 });
          return;
        }

        // Step 2: Put all new data (updates existing, inserts new)
        newData.forEach((item) => {
          const key = item[keyField];
          const isUpdate = existingKeys.has(key);

          const putRequest = store.put(item);

          putRequest.onerror = () => {
            console.error(
              `Failed to put record with key ${key} in ${storeName}:`,
              putRequest.error,
            );
            errors++;
            checkCompletion();
          };

          putRequest.onsuccess = () => {
            if (isUpdate) {
              updated++;
            } else {
              inserted++;
            }
            checkCompletion();
          };
        });

        // Step 3: Delete keys that are no longer present
        keysToDelete.forEach((key) => {
          const deleteRequest = store.delete(key);

          deleteRequest.onerror = () => {
            console.error(
              `Failed to delete record with key ${key} from ${storeName}:`,
              deleteRequest.error,
            );
            errors++;
            checkCompletion();
          };

          deleteRequest.onsuccess = () => {
            deleted++;
            checkCompletion();
          };
        });
      };

      transaction.onerror = () => {
        console.error(
          `Atomic replace transaction failed for ${storeName}:`,
          transaction.error,
        );
        reject(new Error('Atomic replace transaction failed'));
      };
    });
  }

  /**
   * Helper method to evaluate a single filter condition against an item
   */
  private evaluateCondition(item: any, condition: FilterCondition): boolean {
    const fieldValue = item[condition.field];
    const filterValue = condition.value;
    const caseSensitive = condition.caseSensitive ?? false;

    // Handle null/undefined values
    if (fieldValue == null) {
      return (
        filterValue == null &&
        (condition.operator === 'equals' || condition.operator === 'notEquals')
      );
    }

    // Convert values to appropriate types for comparison
    let itemVal: any = fieldValue;
    let filterVal: any = filterValue;

    // For string operations, convert both values to strings and handle case sensitivity
    if (typeof itemVal === 'string' || typeof filterVal === 'string') {
      itemVal = String(itemVal);
      filterVal = String(filterVal);

      if (!caseSensitive) {
        itemVal = itemVal.toLowerCase();
        filterVal = filterVal.toLowerCase();
      }
    }

    switch (condition.operator) {
      case 'contains':
        return itemVal.toString().includes(filterVal.toString());
      case 'notContains':
        return !itemVal.toString().includes(filterVal.toString());
      case 'equals':
        return itemVal === filterVal;
      case 'notEquals':
        return itemVal !== filterVal;
      case 'startsWith':
        return itemVal.toString().startsWith(filterVal.toString());
      case 'endsWith':
        return itemVal.toString().endsWith(filterVal.toString());
      case 'greaterThan':
        return itemVal > filterVal;
      case 'lessThan':
        return itemVal < filterVal;
      case 'greaterThanOrEqual':
        return itemVal >= filterVal;
      case 'lessThanOrEqual':
        return itemVal <= filterVal;
      default:
        console.warn(`Unknown filter operator: ${condition.operator}`);
        return false;
    }
  }

  /**
   * Helper method to evaluate a complex filter against an item
   */
  private evaluateComplexFilter(item: any, filter: ComplexFilter): boolean {
    const conditionResults: boolean[] = [];

    // Evaluate individual conditions
    if (filter.conditions) {
      for (const condition of filter.conditions) {
        conditionResults.push(this.evaluateCondition(item, condition));
      }
    }

    // Evaluate nested filter groups
    if (filter.groups) {
      for (const group of filter.groups) {
        conditionResults.push(this.evaluateComplexFilter(item, group));
      }
    }

    // Apply the operator logic
    if (filter.operator === 'AND') {
      return conditionResults.every((result) => result);
    } else if (filter.operator === 'OR') {
      return conditionResults.some((result) => result);
    }

    return false;
  }

  /**
   * Helper method to check if a filter input is in simple format
   */
  public isSimpleFilter(
    filter: FilterInput,
  ): filter is { [fieldName: string]: string } {
    return (
      filter != null &&
      typeof filter === 'object' &&
      !('operator' in filter) &&
      !('conditions' in filter) &&
      !('groups' in filter)
    );
  }

  /**
   * Helper method to check if a filter input is in complex format
   */
  public isComplexFilter(filter: FilterInput): filter is ComplexFilter {
    return filter != null && typeof filter === 'object' && 'operator' in filter;
  }

  /**
   * Generic filtered search supporting both simple and complex filter formats
   * Optionally use an index to reduce the initial dataset
   *
   * @example Simple filter (backward compatible):
   * getFilteredRecords('inventory', { PartNumber: '123', Description: 'pump' })
   *
   * @example Complex filter with OR logic:
   * getFilteredRecords('inventory', {
   *   operator: 'OR',
   *   conditions: [
   *     { field: 'PartNumber', operator: 'contains', value: '123' },
   *     { field: 'Description', operator: 'contains', value: 'pump' }
   *   ]
   * })
   *
   * @example Complex filter with nested AND/OR:
   * getFilteredRecords('inventory', {
   *   operator: 'AND',
   *   conditions: [
   *     { field: 'LocationID', operator: 'equals', value: '1' }
   *   ],
   *   groups: [{
   *     operator: 'OR',
   *     conditions: [
   *       { field: 'PartNumber', operator: 'startsWith', value: 'ABC' },
   *       { field: 'Description', operator: 'contains', value: 'motor' }
   *     ]
   *   }]
   * })
   */
  async getFilteredRecords<T>(
    storeName: string,
    filters?: FilterInput,
    indexFilter?: { indexName: string; value: any },
  ): Promise<T[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const results: T[] = [];

      // If we have an index filter, use that to reduce the dataset
      let request: IDBRequest<IDBCursorWithValue | null>;

      if (indexFilter) {
        const index = store.index(indexFilter.indexName);
        request = index.openCursor(IDBKeyRange.only(indexFilter.value));
      } else {
        // No index filter, scan all records
        request = store.openCursor();
      }

      request.onerror = () => {
        console.error(
          `Error getting filtered records from ${storeName}:`,
          request.error,
        );
        reject(new Error(`Failed to get filtered records from ${storeName}`));
      };

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const item = cursor.value;
          let includeItem = true;

          // Apply filters based on format
          if (filters && includeItem) {
            if (this.isSimpleFilter(filters)) {
              // Simple filter format (backward compatibility) - all conditions are AND'ed
              for (const [fieldName, searchTerm] of Object.entries(filters)) {
                if (searchTerm) {
                  const fieldValue = (item[fieldName] || '')
                    .toString()
                    .toLowerCase();
                  const lowerSearchTerm = searchTerm.toLowerCase();
                  if (!fieldValue.includes(lowerSearchTerm)) {
                    includeItem = false;
                    break;
                  }
                }
              }
            } else {
              // Complex filter format
              includeItem = this.evaluateComplexFilter(item, filters);
            }
          }

          if (includeItem) {
            results.push(item);
          }

          cursor.continue();
        } else {
          // Finished scanning all records
          resolve(results);
        }
      };
    });
  }
}
